import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  Modal,
  OutlinedInput,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useContext, useEffect, useState } from "react";
import { auth, functions } from "../api_config/firebase-configuration";
import { signInWithEmailAndPassword } from "firebase/auth";
import Link from "next/link";
import { useRouter } from "next/router";
import { setUserPersistence } from "../src/lib/global";
import { LogoOnlyLayout } from "../src/components/Layout";
import {
  FacebookButton,
  GoogleButton,
  TwitterButton,
} from "../src/components/SocialButtons";
import {
  containerBackgroundWithMap,
  containerCard,
  smallModalCard,
} from "../src/styles/globalStyles";
import Head from "next/head";
import { httpsCallable } from "firebase/functions";
import { PasswordResetModal } from "../src/components/PasswordResetModal";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Visibility from "@mui/icons-material/Visibility";
import { useUserData } from "../src/hooks/useUserData";
import { LocalizationContext } from "../src/lib/context";

export default function Enter() {
  const { localeStrings } = useContext(LocalizationContext);
  const localizedPageContent = localeStrings.enterPage;
  const [openResetPass, setOpenResetPass] = useState(false);
  const [pwResetEmail, setPwResetEmail] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const { user, actDroner } = useUserData();
  const [showPassword, setShowPassword] = useState(false);

  const router = useRouter();

  const resetPassFn = httpsCallable(functions, "sendgrid-passwordReset");
  const resetPass = () => {
    resetPassFn({
      email: pwResetEmail,
    }).catch((err) => {
      console.error(err);
    });
    setOpenResetPass(false);
    setPwResetEmail("");
  };

  const signInWithPw = async () => {
    setErrorMessage("");
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (e: any) {
      setErrorMessage(e.code.split("/")[1].split("-").join(" "));
      return null;
    }
    redirectUser();
    return null;
  };

  const redirectUser = useCallback(() => {
    if (user && actDroner) {
      if (!actDroner.username) {
        router.push("/register");
      } else {
        router.push(`/droners/${actDroner.username}`);
      }
    }
  }, [actDroner, router, user]);

  useEffect(() => {
    redirectUser();
  }, [redirectUser]);

  useEffect(() => {
    redirectUser();
  }, [user, actDroner, redirectUser]);

  return (
    <Box
      sx={{
        ...containerBackgroundWithMap,
      }}
    >
      <Head>
        <title>{localizedPageContent.title}</title>
        <meta name="description" content={localizedPageContent.description} />
      </Head>
      <Box
        // @ts-ignore
        sx={{
          ...containerCard,
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          maxWidth: 500,
          gap: 1,
          my: { xs: 5, md: 12 },
          py: { sm: 5, md: 10 },
        }}
      >
        <div className="mb-large">
          <h1 className="subtitle-3 text-center">
            {localizedPageContent.pageTitle}
          </h1>
        </div>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%",
            mx: { xs: 0, sm: 6 },
            px: 2,
          }}
        >
          <TextField
            required
            id="email"
            label={localeStrings.uiGeneral.email}
            name="email"
            autoComplete="email"
            autoFocus
            fullWidth
            onChange={(event: any) => setEmail(event.target.value)}
          />
          <FormControl variant="outlined">
            <InputLabel htmlFor="password">
              {localeStrings.uiGeneral.password}
            </InputLabel>
            <OutlinedInput
              required
              name="password"
              id="password"
              label="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              onChange={(event: any) => setPassword(event.target.value)}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
            />
          </FormControl>
        </Box>

        <FormControlLabel
          control={<Checkbox value="remember" color="primary" defaultChecked />}
          label={localeStrings.uiGeneral.staySignedIn}
          onChange={(event: any) => setUserPersistence(event.target.checked)}
        />
        <Typography variant="inherit" color="error">
          {errorMessage}
        </Typography>
        <div className="w-full flex col px-large gap-normal">
          <button
            type="submit"
            data-variant="login"
            onClick={() => signInWithPw()}
            className="w-full"
          >
            {localeStrings.uiGeneral.logInButton}
          </button>
          <button
            className="link-hover"
            onClick={() => {
              setOpenResetPass(true);
            }}
            data-variant="text"
            data-color="text"
          >
            {localizedPageContent.forgotPasswordButton}?
          </button>
        </div>
        <Box
          sx={{
            width: "100%",
          }}
        >
          <Divider className="py-large">
            <p className="text-text">{localeStrings.uiGeneral.or}</p>
          </Divider>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minWidth: 100,
            px: 2,
            width: "100%",
            gap: 1,
          }}
        >
          <GoogleButton
            action="login"
            text={localeStrings?.enterPage.googleLogInButton}
          />
          <FacebookButton
            action="login"
            text={localeStrings?.enterPage.facebookLogInButton}
          />
          <TwitterButton
            action="login"
            text={localeStrings?.enterPage.twitterLogInButton}
          />
          {/* <YahooButton />*/}
        </Box>
        <div className="w-full flex col px-large gap-normal">
          <p className="my-large text-text text-center">
            {localizedPageContent.notAMemberLink}?
          </p>
          <Link href="/register">
            <Button variant="contained" data-variant="login">
              {localizedPageContent.registerLink}
            </Button>
          </Link>
        </div>
      </Box>
      <Modal open={openResetPass} onClose={() => setOpenResetPass(false)}>
        <Box sx={{ ...smallModalCard, minWidth: 300 }}>
          <PasswordResetModal
            email={pwResetEmail}
            setEmail={setPwResetEmail}
            resetPass={resetPass}
          />
        </Box>
      </Modal>
    </Box>
  );
}

Enter.Layout = LogoOnlyLayout;
